import { forwardRef, useEffect, useId, useMemo, useRef, useState } from 'react'
import type { ChangeEvent, InputHTMLAttributes, MouseEvent as ReactMouseEvent } from 'react'
import { createPortal } from 'react-dom'
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isBefore,
  isSameDay,
  isValid,
  parseISO,
  startOfMonth,
  subMonths,
} from 'date-fns'
import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { cn } from '@/lib/cn'

export interface DateInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string
  error?: string
}

const baseClasses =
  'box-border flex h-11 w-full items-center justify-between rounded-xl border border-gray-200 bg-white/95 px-3.5 text-left text-sm text-gray-900 shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-all duration-150 placeholder:text-gray-400 focus:border-primary/40 focus:outline-none focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800/90 dark:text-gray-100 dark:placeholder:text-gray-500'

const toDate = (value: string | undefined): Date | null => {
  if (!value) return null
  const parsed = parseISO(value)
  return isValid(parsed) ? parsed : null
}

const toInputEvent = (value: string, name?: string): ChangeEvent<HTMLInputElement> => {
  // A plain text input round-trips any string verbatim. A `type="date"` input
  // normalizes/rejects values that aren't a browser-valid date (locale quirks,
  // empty string), which silently drops the value RHF reads from event.target.value.
  const target = document.createElement('input')
  target.type = 'text'
  target.name = name ?? ''
  target.value = value
  return { target, currentTarget: target, type: 'change' } as ChangeEvent<HTMLInputElement>
}

const getPopoverPosition = (trigger: DOMRect): { top: number; left: number } => {
  const width = 288
  const estimatedHeight = 355
  return {
    top: Math.max(8, Math.min(trigger.bottom + 8, window.innerHeight - estimatedHeight - 8)),
    left: Math.max(8, Math.min(trigger.left, window.innerWidth - width - 8)),
  }
}

/** Shadcn-style calendar input that keeps the existing yyyy-MM-dd form value API. */
export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  (
    {
      label,
      error,
      className,
      id: idProp,
      name,
      value,
      defaultValue,
      onChange,
      onBlur,
      min,
      max,
      disabled = false,
      placeholder = 'dd/mm/yyyy',
      required,
      ...rest
    },
    ref
  ): React.ReactElement => {
    const generatedId = useId()
    const id = idProp ?? name ?? generatedId
    const containerRef = useRef<HTMLDivElement>(null)
    const hiddenInputRef = useRef<HTMLInputElement | null>(null)
    const initialValue = typeof defaultValue === 'string' ? defaultValue : ''
    const controlledValue = typeof value === 'string' ? value : undefined
    const [internalValue, setInternalValue] = useState(initialValue)
    const currentValue = controlledValue ?? internalValue
    const selectedDate = toDate(currentValue)

    // Merge the forwarded ref (react-hook-form uses it to read/write .value) with
    // our own, so imperative writes like RHF's reset() can be observed below.
    const setInputRef = (node: HTMLInputElement | null): void => {
      hiddenInputRef.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node
    }

    const [month, setMonth] = useState(() => startOfMonth(selectedDate ?? new Date()))
    const [open, setOpen] = useState(false)
    const buttonRef = useRef<HTMLButtonElement>(null)
    const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 })
    const minDate = toDate(typeof min === 'string' ? min : undefined)
    const maxDate = toDate(typeof max === 'string' ? max : undefined)

    // In register-mode (no controlled `value`), react-hook-form's reset()/setValue
    // write straight to the DOM node's .value and re-render this subtree without
    // giving us the new value via props. This is the "subscribe to an external
    // system" case effects exist for: mirror the externally-written DOM value back
    // into state so the button label and calendar selection stay in sync. Runs on
    // every render (no dep array) because RHF's imperative write leaves no React
    // signal to key on; guarded by an equality check so it can't loop.
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally runs
       every render to observe RHF's imperative DOM writes; guarded against loops */
    useEffect(() => {
      if (controlledValue !== undefined) return
      const domValue = hiddenInputRef.current?.value ?? ''
      if (domValue !== internalValue) setInternalValue(domValue)
    })

    useEffect(() => {
      if (selectedDate) setMonth(startOfMonth(selectedDate))
    }, [currentValue])

    useEffect(() => {
      const handlePointerDown = (event: MouseEvent): void => {
        const target = event.target as Element | null
        // The calendar is portaled to document.body, so it is not a child of
        // containerRef. Keep it open long enough for its date button's click
        // handler to commit the selected value.
        if (target?.closest('[data-date-calendar]')) return
        if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
      }
      document.addEventListener('mousedown', handlePointerDown)
      return () => document.removeEventListener('mousedown', handlePointerDown)
    }, [])

    useEffect(() => {
      if (!open) return
      const updatePopoverPosition = (): void => {
        const trigger = buttonRef.current?.getBoundingClientRect()
        if (!trigger) return
        setPopoverPosition(getPopoverPosition(trigger))
      }
      updatePopoverPosition()
      window.addEventListener('resize', updatePopoverPosition)
      window.addEventListener('scroll', updatePopoverPosition, true)
      return () => {
        window.removeEventListener('resize', updatePopoverPosition)
        window.removeEventListener('scroll', updatePopoverPosition, true)
      }
    }, [open])

    const days = useMemo(() => {
      const firstDayOffset = getDay(startOfMonth(month))
      return [
        ...Array.from({ length: firstDayOffset }, () => null),
        ...eachDayOfInterval({ start: startOfMonth(month), end: endOfMonth(month) }),
      ]
    }, [month])

    const isAllowed = (date: Date): boolean => {
      if (minDate && isBefore(date, minDate)) return false
      if (maxDate && isBefore(maxDate, date)) return false
      return true
    }

    const updateValue = (nextValue: string): void => {
      if (controlledValue === undefined) {
        setInternalValue(nextValue)
        // Uncontrolled/register-mode: keep the hidden DOM field synchronized
        // for RHF reset/getValues, then call the registered handler once with
        // the normalized ISO value. Mixing a synthetic handler with a native
        // dispatched event caused duplicate updates and modal flicker.
        if (hiddenInputRef.current) {
          hiddenInputRef.current.value = nextValue
        }
        onChange?.(toInputEvent(nextValue, name))
        setOpen(false)
        return
      }
      onChange?.(toInputEvent(nextValue, name))
      setOpen(false)
    }

    const handleClear = (event: ReactMouseEvent<HTMLButtonElement>): void => {
      event.stopPropagation()
      updateValue('')
    }

    return (
      <div ref={containerRef} className="relative w-full">
        {label && (
          <label
            htmlFor={id}
            className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {label}
            {required && <span className="text-danger"> *</span>}
          </label>
        )}
        <input
          ref={setInputRef}
          id={`${id}-value`}
          name={name}
          // Controlled when a `value` prop is supplied; otherwise register-mode:
          // leave it uncontrolled so RHF's ref writes (reset/setValue) persist.
          {...(controlledValue !== undefined
            ? { value: controlledValue }
            : { defaultValue: initialValue })}
          onChange={onChange}
          onBlur={onBlur}
          min={min}
          max={max}
          // No native `required` here: this input is visually hidden and not
          // focusable, so the browser's validation bubble would point at
          // nothing. Like <Input>, `required` only marks the field (asterisk +
          // aria-required); forms enforce it and show the field error.
          disabled={disabled}
          tabIndex={-1}
          aria-hidden="true"
          className="sr-only"
          {...rest}
        />
        <button
          type="button"
          ref={buttonRef}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={`${id}-calendar`}
          id={id}
          aria-invalid={error ? 'true' : undefined}
          aria-required={required || undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          aria-label={label ?? name ?? 'Choose date'}
          disabled={disabled}
          onClick={() => {
            if (!open && buttonRef.current) {
              setPopoverPosition(getPopoverPosition(buttonRef.current.getBoundingClientRect()))
            }
            setOpen((current) => !current)
          }}
          className={cn(
            baseClasses,
            error ? 'border-danger focus:border-danger/70 focus:ring-danger/10' : '',
            !selectedDate && 'text-gray-400 dark:text-gray-500',
            className
          )}
        >
          <span>{selectedDate ? format(selectedDate, 'dd/MM/yyyy') : placeholder}</span>
          <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
            {selectedDate && !disabled && (
              <span
                role="button"
                tabIndex={0}
                aria-label="Clear date"
                onClick={handleClear}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') updateValue('')
                }}
                className="rounded p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="h-4 w-4" aria-hidden />
              </span>
            )}
            <CalendarDays className="h-4 w-4" aria-hidden />
          </span>
        </button>
        {open &&
          !disabled &&
          createPortal(
            <div
              id={`${id}-calendar`}
              data-date-calendar
              role="dialog"
              aria-label="Choose date"
              style={{ top: popoverPosition.top, left: popoverPosition.left }}
              className="fixed z-[100] w-[18rem] rounded-xl border border-gray-200 bg-white p-3 shadow-xl dark:border-gray-700 dark:bg-gray-900"
            >
              <div className="mb-3 flex items-center justify-between">
                <button
                  type="button"
                  aria-label="Previous month"
                  onClick={() => setMonth((current) => subMonths(current, 1))}
                  className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden />
                </button>
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {format(month, 'MMMM yyyy')}
                </span>
                <button
                  type="button"
                  aria-label="Next month"
                  onClick={() => setMonth((current) => addMonths(current, 1))}
                  className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <ChevronRight className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <div className="grid grid-cols-7 text-center text-[11px] font-medium text-gray-500 dark:text-gray-400">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                  <span key={day} className="py-1">
                    {day}
                  </span>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1 text-center">
                {days.map((date, index) => (
                  <span
                    key={date ? date.toISOString() : `empty-${index}`}
                    className="aspect-square"
                  >
                    {date && (
                      <button
                        type="button"
                        disabled={!isAllowed(date)}
                        onClick={() => updateValue(format(date, 'yyyy-MM-dd'))}
                        className={cn(
                          'h-full w-full rounded-md text-xs transition-colors hover:bg-primary/10 disabled:pointer-events-none disabled:text-gray-300 dark:disabled:text-gray-700',
                          selectedDate && isSameDay(date, selectedDate)
                            ? 'bg-primary text-white hover:bg-primary/90'
                            : 'text-gray-700 dark:text-gray-200'
                        )}
                      >
                        {format(date, 'd')}
                      </button>
                    )}
                  </span>
                ))}
              </div>
              <div className="mt-3 flex justify-between border-t border-gray-100 pt-2 dark:border-gray-800">
                <button
                  type="button"
                  onClick={() => updateValue('')}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const today = new Date()
                    if (isAllowed(today)) updateValue(format(today, 'yyyy-MM-dd'))
                  }}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Today
                </button>
              </div>
            </div>,
            document.body
          )}
        {error && (
          <p id={`${id}-error`} className="mt-1 text-sm text-danger">
            {error}
          </p>
        )}
      </div>
    )
  }
)

DateInput.displayName = 'DateInput'
